import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BASE_URL } from "../../constants";
import AuthFrame from "./v2/AuthFrame";
import { TitleBlock, Note, Actions, PrimaryLink } from "./v2/pieces";

export const CLAIMED_MESSAGE = "Email confirmed. Use Forgot password to set your password, then log in.";

// What each outcome shows: the title, the line under it and the one way on.
// A claimed account had its password cleared, so it goes to Forgot
// password; an expired link asks for a new one on the check-email page.
const OUTCOMES = {
  loading: { title: "Confirming your email", sub: "One moment..." },
  success: { title: "You're verified", sub: "Redirecting you to log in...", link: { to: "/login", label: "Go to log in" } },
  already: { title: "Already verified", sub: "Redirecting you to log in...", link: { to: "/login", label: "Go to log in" } },
  claimed: { title: "One more step", link: { to: "/forgot-password", label: "Set a password" } },
  expired: { title: "Link expired", tone: "critical", link: { to: "/check-email", label: "Request a new link" } },
  error: { title: "Verification failed", tone: "critical", link: { to: "/login", label: "Back to log in" } },
};

// The page the confirmation link in the welcome email opens (old link
// flow), on the V2 auth frame. It asks the backend to confirm the email,
// then goes to log in. When the link took over an account opened with a
// mobile code, its password was cleared, so the page stays put and points
// to Forgot password instead.
const VerifyEmail = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "loading", message: "" });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", message: "Missing verification token." });
      return;
    }
    fetch(`${BASE_URL}/api/users/verify-email/?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (res.ok && (data.claimed || data.passwordCleared)) {
          // The link proved the email of an account opened with a mobile
          // code: the password set then no longer works, so log in would
          // fail. Stay here and point to Forgot password instead.
          setState({ status: "claimed", message: data.message || CLAIMED_MESSAGE });
        } else if (res.ok) {
          setState({ status: data.alreadyVerified ? "already" : "success", message: data.message || "" });
          setTimeout(() => navigate("/login"), 2500);
        } else if (res.status === 410) {
          setState({ status: "expired", message: data.message || "Link expired.", email: data.email });
        } else {
          setState({ status: "error", message: data.message || "Invalid verification link." });
        }
      })
      .catch(() => setState({ status: "error", message: "Network error. Please try again." }));
  }, [token, navigate]);

  const outcome = OUTCOMES[state.status];
  // The redirecting outcomes keep their own line; the others show the server's words.
  const sub = outcome.sub || state.message;
  return (
    <AuthFrame title="Confirm your email" onBack={() => navigate("/login")}>
      <TitleBlock eyebrow="Verification" tone={outcome.tone} title={outcome.title} sub={outcome.tone ? null : sub} />
      {outcome.tone && sub && <Note tone="critical">{sub}</Note>}
      {outcome.link && (
        <Actions>
          <PrimaryLink to={outcome.link.to} state={state.status === "expired" ? { email: state.email } : undefined}>{outcome.link.label}</PrimaryLink>
        </Actions>
      )}
    </AuthFrame>
  );
};

export default VerifyEmail;
